import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { AiSuggestionsPlugin, Suggestion } from '../aiSuggestionsPluginType';
import { fireSubmitSuggestionFeedbackUnifiedAnalytics } from '../ui/utils/analytics';
import { getPositionsByLocalId } from '../ui/utils/toolCalls/applyToolCalls';
import type {
	OnSubmitSuggestionFeedback,
	SubmitSuggestionFeedbackAction,
	SuggestionFeedbackContext,
} from './types';

// Number of top-level blocks either side of a targeted block to include for context.
const ADJACENT_NODE_RADIUS = 1;

// Collect every localId a suggestion targets, across all tool-call kinds and
// remix actions, de-duplicated and in first-seen order.
const collectTargetLocalIds = (suggestion: Suggestion): string[] => {
	const ids: string[] = [];
	const add = (id: string | undefined) => {
		if (id && !ids.includes(id)) {
			ids.push(id);
		}
	};
	for (const toolCall of suggestion.toolCalls ?? []) {
		if (toolCall.name === 'removeNodes') {
			toolCall.arguments.localIds.forEach(add);
		} else {
			add(toolCall.arguments.localId);
		}
	}
	suggestion.action?.localIds?.forEach(add);
	return ids;
};

// Build a minimal ADF doc from the targeted top-level block(s) plus adjacent siblings (de-duped, in document order).
const buildTargetNodeAdf = (
	doc: PMNode,
	suggestion: Suggestion,
): SuggestionFeedbackContext['documentAdf'] => {
	const radius = ADJACENT_NODE_RADIUS;
	const lastIndex = doc.childCount - 1;

	// Collect top-level indices to emit, merging each targeted node's adjacency window.
	const selectedIndices = new Set<number>();
	for (const localId of collectTargetLocalIds(suggestion)) {
		const positions = getPositionsByLocalId(doc, localId);
		if (!positions) {
			continue;
		}
		// Top-level block (direct child of doc) containing this position.
		const topLevelIndex = doc.resolve(positions.from).index(0);
		const start = Math.max(0, topLevelIndex - radius);
		const end = Math.min(lastIndex, topLevelIndex + radius);
		for (let index = start; index <= end; index++) {
			selectedIndices.add(index);
		}
	}

	// Emit whole blocks in document order.
	const content: Record<string, unknown>[] = [];
	for (const index of [...selectedIndices].sort((a, b) => a - b)) {
		content.push(doc.child(index).toJSON() as Record<string, unknown>);
	}

	return content.length > 0 ? { version: 1, type: 'doc', content } : undefined;
};

/**
 * Submit user feedback (good / bad) for a suggestion: fires the unified feedback
 * event and forwards to the product's `onSubmitSuggestionFeedback` callback when
 * supplied. Returns `false` when the suggestion no longer exists.
 *
 * TODO: EDITOR-8269 - Use the backend-assigned instrumentation id instead of the
 * suggestion id for `singleInstrumentationID`.
 */
export const submitSuggestionFeedbackAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	onSubmitSuggestionFeedback?: OnSubmitSuggestionFeedback,
) => SubmitSuggestionFeedbackAction =
	(
		api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
		onSubmitSuggestionFeedback?: OnSubmitSuggestionFeedback,
	) =>
	(id, sentiment, options): boolean => {
		const pluginState = api?.aiSuggestions?.sharedState.currentState();
		const suggestion = pluginState?.suggestionById.get(id);
		if (!suggestion) {
			return false;
		}
		const interactionPoint = options?.interactionPoint ?? 'card';
		fireSubmitSuggestionFeedbackUnifiedAnalytics(
			api?.analytics?.actions,
			sentiment,
			id,
			interactionPoint,
		);

		// Snapshot the ADF of just the targeted node(s) so the product can save a
		// compact artifact for reproducing the suggestion locally.
		let documentAdf: SuggestionFeedbackContext['documentAdf'];
		api?.core?.actions.execute(({ tr }) => {
			documentAdf = buildTargetNodeAdf(tr.doc, suggestion);
			return tr;
		});

		// Forward feedback plus the full payload and targeted node ADF.
		onSubmitSuggestionFeedback?.({
			interactionPoint,
			sentiment,
			suggestionId: id,
			suggestionType: suggestion.type,
			suggestion,
			documentAdf,
		});
		return true;
	};
